/**
 * Navigation Store
 *
 * Zustand store for navigation state management
 */

import { create } from 'zustand';
import type { NavigationState, NavigationStore, BreadcrumbItem, NavigationItem } from '@/types';
import { defaultNavItems } from '../config';
import {
  ASSISTANT_PANEL_DEFAULT_WIDTH,
  clampAssistantPanelWidth,
} from '@/features/assistant/panelWidth';

export { defaultNavItems } from '../config';

/**
 * Initial navigation state
 */
const initialState: NavigationState = {
  currentPath: window.location.pathname,
  previousPath: null,
  breadcrumbs: [],
  sidebarCollapsed: false,
  activeServiceId: null,
  mobileMenuOpen: false,
  assistantOpen: false,
  assistantPanelWidth: ASSISTANT_PANEL_DEFAULT_WIDTH,
};

/**
 * Extended store type to include navigation items management
 */
export interface ExtendedNavigationStore extends NavigationStore {
  items: NavigationItem[];
  registerNavItem: (item: NavigationItem) => void;
  removeNavItem: (id: string) => void;
  setNavItems: (items: NavigationItem[]) => void;
}

/**
 * Navigation store implementation
 */
export const useNavigationStore = create<ExtendedNavigationStore>((set, get) => ({
  ...initialState,
  items: defaultNavItems,

  navigate: (path: string) => {
    set((state) => ({
      previousPath: state.currentPath,
      currentPath: path,
    }));
  },

  setBreadcrumbs: (items: BreadcrumbItem[]) => {
    set({ breadcrumbs: items });
  },

  toggleSidebar: () => {
    set((state) => ({
      sidebarCollapsed: !state.sidebarCollapsed,
      assistantOpen: state.sidebarCollapsed ? false : state.assistantOpen,
    }));
  },

  setSidebarCollapsed: (collapsed: boolean) => {
    set((state) => ({
      sidebarCollapsed: collapsed,
      assistantOpen: collapsed ? state.assistantOpen : false,
    }));
  },

  setActiveService: (serviceId: string | null) => {
    set({ activeServiceId: serviceId });
  },

  toggleMobileMenu: () => {
    set((state) => ({
      mobileMenuOpen: !state.mobileMenuOpen,
    }));
  },

  setMobileMenuOpen: (open: boolean) => {
    set({ mobileMenuOpen: open });
  },

  openAssistant: () => set({ assistantOpen: true, sidebarCollapsed: true, mobileMenuOpen: false }),
  closeAssistant: () => set({ assistantOpen: false }),
  toggleAssistant: () => {
    if (get().assistantOpen) get().closeAssistant();
    else get().openAssistant();
  },
  setAssistantPanelWidth: (width) =>
    set({ assistantPanelWidth: clampAssistantPanelWidth(width, window.innerWidth) }),
  resetAssistantPanelWidth: () => set({ assistantPanelWidth: ASSISTANT_PANEL_DEFAULT_WIDTH }),

  // Extended actions
  registerNavItem: (item: NavigationItem) => {
    set((state) => {
      // Check if item already exists to avoid duplicates
      const exists = state.items.some((i) => i.id === item.id);
      if (exists) return state;

      return {
        items: [...state.items, item].sort((a, b) => (a.order || 0) - (b.order || 0)),
      };
    });
  },

  removeNavItem: (id: string) => {
    set((state) => ({
      items: state.items.filter((item) => item.id !== id),
    }));
  },

  setNavItems: (items: NavigationItem[]) => {
    set({ items });
  },
}));

export default useNavigationStore;
